/**
 * Audit Logger Utility
 * Handles cart change detection and audit event logging
 * Enhanced with buffer management, snapshot system, and event-driven flushing
 */

export interface CartItem {
    id: string;
    name: string;
    amount: number;
    price: number;
    selectedOption?: string;
    advancedOptions?: any;
    seatNumber?: number;
    [key: string]: any; // Allow additional properties
}

export interface AuditEvent {
    eventId?: string;
    type: 'KITCHEN_PRINT' | 'ITEM_ADDED' | 'ITEM_REMOVED' | 'PRICE_CHANGE' | 'PRINT_FO' | 'PAYMENT' | 'CART_MODIFIED' | 'CANCELLATION' | 'CORRECTION' | 'OFFLINE_ERROR' | 'SYSTEM_MESSAGE';
    timestamp: number;
    user: string;
    sessionId?: string;
    snapshotBefore?: AuditSnapshot;
    snapshotAfter?: AuditSnapshot;
    changes?: {
        added: CartItem[];
        removed: CartItem[];
        modified: ItemModification[];
    };
    details: {
        itemName?: string;
        quantity?: number;
        price?: number;
        oldPrice?: number;
        transactionNumber?: string;
        apiResponse?: any;
        cartHash?: string;
        paymentMethod?: string;
        amount?: number;
        error?: string;
        message?: string;
        cancellationReason?: string;
    };
}

export interface ItemModification {
    item: CartItem;
    field: 'price' | 'quantity' | 'option' | 'discount' | 'note';
    oldValue: any;
    newValue: any;
}

export interface AuditSnapshot {
    snapshotId: string;
    timestamp: number;
    cartState: CartItem[];
    totals?: {
        subtotal: number;
        total: number;
        taxes?: any[];
    };
    metadata?: {
        sessionId?: string;
        tableName?: string;
        customerName?: string;
        remarks?: string;
        [key: string]: any;
    };
}

export interface PendingChange {
    type: 'ITEM_ADDED' | 'ITEM_REMOVED' | 'PRICE_CHANGE' | 'QUANTITY_CHANGE' | 'OPTION_CHANGE' | 'DISCOUNT_CHANGE' | 'NOTE_CHANGE';
    timestamp: number;
    item: CartItem;
    previousValue?: any;
    previousState?: CartItem;
}

export interface AuditBuffer {
    sessionId: string;
    lastSnapshot: AuditSnapshot | null;
    pendingChanges: PendingChange[];
    initializedAt: number;
    lastFlushAt?: number;
}

export interface PrintEvent {
    printNumber: number;
    transactionNumber: string;
    timestamp: number;
    user: string;
    cartHash: string;
    isRevised: boolean;
    replacesCount: number;
}

/**
 * Generate a deterministic hash from cart items
 * This hash is used to detect cart changes between prints
 */
export function generateCartHash(items: CartItem[]): string {
    if (!items || items.length === 0) return '';

    // Sort items by ID to ensure consistent ordering
    const sortedItems = [...items].sort((a, b) => {
        const idCompare = a.id.localeCompare(b.id);
        if (idCompare !== 0) return idCompare;

        // If same ID, sort by selected option
        const optionA = a.selectedOption || '';
        const optionB = b.selectedOption || '';
        return optionA.localeCompare(optionB);
    });

    // Create hash string from item properties
    const hashString = sortedItems.map(item => {
        const option = item.selectedOption || 'none';
        const seat = item.seatNumber || 0;
        const advOpts = item.advancedOptions ? JSON.stringify(item.advancedOptions) : 'none';

        return `${item.id}:${item.name}:${item.amount}:${item.price}:${option}:${seat}:${advOpts}`;
    }).join('|');

    // Simple hash function (for deterministic results)
    return simpleHash(hashString);
}

/**
 * Simple hash function for strings
 */
function simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
}

/**
 * Compare two cart states and identify specific changes
 */
export function detectCartChanges(
    previousItems: CartItem[],
    currentItems: CartItem[]
): { added: CartItem[]; removed: CartItem[]; priceChanged: CartItem[] } {
    const added: CartItem[] = [];
    const removed: CartItem[] = [];
    const priceChanged: CartItem[] = [];

    // Create lookup maps for comparison
    const prevMap = new Map<string, CartItem>();
    const currMap = new Map<string, CartItem>();

    previousItems.forEach(item => {
        const key = getItemKey(item);
        prevMap.set(key, item);
    });

    currentItems.forEach(item => {
        const key = getItemKey(item);
        currMap.set(key, item);
    });

    // Find added items
    currMap.forEach((item, key) => {
        if (!prevMap.has(key)) {
            added.push(item);
        } else {
            // Check for price changes
            const prevItem = prevMap.get(key)!;
            if (prevItem.price !== item.price) {
                priceChanged.push(item);
            }
        }
    });

    // Find removed items
    prevMap.forEach((item, key) => {
        if (!currMap.has(key)) {
            removed.push(item);
        }
    });

    return { added, removed, priceChanged };
}

/**
 * Generate a unique key for an item
 */
function getItemKey(item: CartItem): string {
    const option = item.selectedOption || 'none';
    const seat = item.seatNumber || 0;
    const advOpts = item.advancedOptions ? JSON.stringify(item.advancedOptions) : 'none';

    return `${item.id}:${option}:${seat}:${advOpts}`;
}

/**
 * Format audit event for display
 */
export function formatAuditEvent(event: AuditEvent): string {
    const date = new Date(event.timestamp);
    const time = date.toLocaleTimeString();

    switch (event.type) {
        case 'KITCHEN_PRINT':
            return `${time} - Kitchen Print by ${event.user}`;

        case 'ITEM_ADDED':
            return `${time} - Added: ${event.details.itemName} (x${event.details.quantity}) by ${event.user}`;

        case 'ITEM_REMOVED':
            return `${time} - Removed: ${event.details.itemName} (x${event.details.quantity}) by ${event.user}`;

        case 'PRICE_CHANGE':
            return `${time} - Price changed: ${event.details.itemName} ($${event.details.oldPrice} → $${event.details.price}) by ${event.user}`;

        case 'PRINT_FO':
            return `${time} - Print FO: ${event.details.transactionNumber} by ${event.user}`;

        case 'PAYMENT':
            return `${time} - Payment completed by ${event.user}`;

        case 'CART_MODIFIED':
            return `${time} - Cart modified by ${event.user}`;

        default:
            return `${time} - ${event.type} by ${event.user}`;
    }
}

/**
 * Calculate the number of bills this revised bill replaces
 */
export function calculateReplacesCount(printHistory: PrintEvent[]): number {
    // Count how many previous prints exist
    return printHistory.length;
}

/**
 * Generate transaction number with suffix
 */
export function generateTransactionNumber(
    baseSessionId: string,
    suffix: number
): string {
    if (suffix === 0) {
        return baseSessionId;
    }
    return `${baseSessionId}-${suffix}`;
}

// ==================== AUDIT BUFFER MANAGEMENT ====================

// In-memory buffer store: Map<sessionId, AuditBuffer>
const auditBuffers = new Map<string, AuditBuffer>();

/**
 * Initialize audit buffer for a session
 * Called when session is created
 */
export function initializeAuditBuffer(
    sessionId: string,
    initialCartState: CartItem[] = [],
    metadata?: any
): void {
    const snapshot = createCartSnapshot(initialCartState, sessionId, metadata);
    
    auditBuffers.set(sessionId, {
        sessionId,
        lastSnapshot: snapshot,
        pendingChanges: [],
        initializedAt: Date.now()
    });
    
    console.log(`[Audit] Buffer initialized for session: ${sessionId}`);
}

/**
 * Create a cart snapshot (complete state at a point in time)
 */
export function createCartSnapshot(
    items: CartItem[],
    sessionId: string,
    metadata?: any,
    totals?: any
): AuditSnapshot {
    return {
        snapshotId: `${sessionId}-${Date.now()}`,
        timestamp: Date.now(),
        cartState: items.map(item => ({ ...item })), // Deep copy
        totals: totals ? {
            subtotal: totals.subtotal || 0,
            total: totals.total || 0,
            taxes: totals.taxes || []
        } : undefined,
        metadata: metadata ? { ...metadata, sessionId } : { sessionId }
    };
}

/**
 * Buffer a cart change (non-blocking, synchronous)
 * This is called on every cart operation but doesn't calculate diffs yet
 */
export function bufferCartChange(
    sessionId: string,
    type: PendingChange['type'],
    item: CartItem,
    previousState?: CartItem
): void {
    const buffer = auditBuffers.get(sessionId);
    if (!buffer) {
        // Buffer not initialized, skip silently (session might not be ready)
        return;
    }

    const change: PendingChange = {
        type,
        timestamp: Date.now(),
        item: { ...item }, // Copy item
        previousState: previousState ? { ...previousState } : undefined
    };

    buffer.pendingChanges.push(change);
    
    // Keep buffer size reasonable (prevent memory issues)
    if (buffer.pendingChanges.length > 1000) {
        // Remove oldest changes, keep last 500
        buffer.pendingChanges = buffer.pendingChanges.slice(-500);
    }
}

/**
 * Calculate changes between two snapshots (lazy evaluation)
 * Only called when flushing, not on every operation
 */
export function calculateChangesBetweenSnapshots(
    prevSnapshot: AuditSnapshot | null,
    currentSnapshot: AuditSnapshot
): { added: CartItem[]; removed: CartItem[]; modified: ItemModification[] } {
    if (!prevSnapshot) {
        // First snapshot - everything is "added"
        return {
            added: currentSnapshot.cartState.map(item => ({ ...item })),
            removed: [],
            modified: []
        };
    }

    const prevItems = prevSnapshot.cartState;
    const currItems = currentSnapshot.cartState;

    // Use existing detectCartChanges for added/removed
    const changes = detectCartChanges(prevItems, currItems);

    // Calculate modifications (price, quantity changes)
    const modified: ItemModification[] = [];
    const prevMap = new Map<string, CartItem>();
    const currMap = new Map<string, CartItem>();

    prevItems.forEach(item => {
        prevMap.set(getItemKey(item), item);
    });

    currItems.forEach(item => {
        currMap.set(getItemKey(item), item);
    });

    // Check for modifications in existing items
    currMap.forEach((currItem, key) => {
        const prevItem = prevMap.get(key);
        if (prevItem) {
            // Item exists in both - check for changes
            if (prevItem.price !== currItem.price) {
                modified.push({
                    item: { ...currItem },
                    field: 'price',
                    oldValue: prevItem.price,
                    newValue: currItem.price
                });
            }
            if (prevItem.amount !== currItem.amount) {
                modified.push({
                    item: { ...currItem },
                    field: 'quantity',
                    oldValue: prevItem.amount,
                    newValue: currItem.amount
                });
            }
            if (prevItem.selectedOption !== currItem.selectedOption) {
                modified.push({
                    item: { ...currItem },
                    field: 'option',
                    oldValue: prevItem.selectedOption,
                    newValue: currItem.selectedOption
                });
            }
            if ((prevItem.itemDiscount || 0) !== (currItem.itemDiscount || 0)) {
                modified.push({
                    item: { ...currItem },
                    field: 'discount',
                    oldValue: prevItem.itemDiscount || 0,
                    newValue: currItem.itemDiscount || 0
                });
            }
            if ((prevItem.itemNote || '') !== (currItem.itemNote || '')) {
                modified.push({
                    item: { ...currItem },
                    field: 'note',
                    oldValue: prevItem.itemNote || '',
                    newValue: currItem.itemNote || ''
                });
            }
        }
    });

    return {
        added: changes.added.map(item => ({ ...item })),
        removed: changes.removed.map(item => ({ ...item })),
        modified
    };
}

/**
 * Flush audit buffer (calculate diffs and create audit event)
 * Called on significant events: print, payment, cancellation, corrections
 */
export async function flushAuditBuffer(
    sessionId: string,
    eventType: AuditEvent['type'],
    currentCartState: CartItem[],
    user: string,
    metadata?: any,
    totals?: any,
    eventDetails?: any
): Promise<AuditEvent | null> {
    const buffer = auditBuffers.get(sessionId);
    if (!buffer) {
        console.warn(`[Audit] No buffer found for session: ${sessionId}`);
        return null;
    }

    // Create current snapshot
    const currentSnapshot = createCartSnapshot(currentCartState, sessionId, metadata, totals);
    
    // Calculate changes between last snapshot and current state
    const changes = calculateChangesBetweenSnapshots(buffer.lastSnapshot, currentSnapshot);

    // Create audit event
    const auditEvent: AuditEvent = {
        eventId: `audit-${sessionId}-${Date.now()}`,
        type: eventType,
        timestamp: Date.now(),
        user,
        sessionId,
        snapshotBefore: buffer.lastSnapshot,
        snapshotAfter: currentSnapshot,
        changes: {
            added: changes.added,
            removed: changes.removed,
            modified: changes.modified
        },
        details: {
            ...eventDetails
        }
    };

    // Update buffer: set current snapshot as last snapshot, clear pending changes
    buffer.lastSnapshot = currentSnapshot;
    buffer.pendingChanges = [];
    buffer.lastFlushAt = Date.now();

    console.log(`[Audit] Buffer flushed for session: ${sessionId}, event: ${eventType}, changes: ${changes.added.length} added, ${changes.removed.length} removed, ${changes.modified.length} modified`);

    // Queue for Firebase sync (non-blocking)
    try {
        const { queueAuditEventForSync } = await import('./auditFirebaseSync');
        queueAuditEventForSync(sessionId, auditEvent).catch(err => {
            console.error('[Audit] Failed to queue event for Firebase sync:', err);
        });
    } catch (err) {
        console.error('[Audit] Error importing Firebase sync service:', err);
    }

    return auditEvent;
}

/**
 * Get current buffer state (for debugging)
 */
export function getAuditBuffer(sessionId: string): AuditBuffer | undefined {
    return auditBuffers.get(sessionId);
}

/**
 * Clear audit buffer (when session ends)
 */
export function clearAuditBuffer(sessionId: string): void {
    auditBuffers.delete(sessionId);
    console.log(`[Audit] Buffer cleared for session: ${sessionId}`);
}

/**
 * Create an immediate audit event (for errors, system messages)
 * These are written immediately without buffering
 */
export function createImmediateAuditEvent(
    sessionId: string,
    type: 'OFFLINE_ERROR' | 'SYSTEM_MESSAGE',
    user: string,
    message: string,
    error?: any
): AuditEvent {
    return {
        eventId: `audit-${sessionId}-${Date.now()}`,
        type,
        timestamp: Date.now(),
        user,
        sessionId,
        details: {
            message,
            error: error ? (error.message || String(error)) : undefined
        }
    };
}
